import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, FileText, CheckCircle, Clock, AlertCircle } from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === '') return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('it-IT');
};

const STATI_FATTURA = [
  { value: "ricevuta", label: "Ricevuta", color: "bg-[#007AFF]/10 text-[#007AFF]" },
  { value: "pagata", label: "Pagata", color: "bg-[#34C759]/10 text-[#34C759]" },
  { value: "scaduta", label: "Scaduta", color: "bg-[#FF3B30]/10 text-[#FF3B30]" }
];

const FatturePassive = () => {
  const { getAuthHeaders } = useAuth();
  const [fatture, setFatture] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statoFilter, setStatoFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFattura, setEditingFattura] = useState(null);
  const [formData, setFormData] = useState({
    numero: "",
    fornitore: "",
    descrizione: "",
    imponibile: "",
    iva: "22",
    totale: "",
    data_ricezione: new Date().toISOString().split("T")[0],
    data_scadenza: "",
    stato: "ricevuta",
    metodo_pagamento: "",
    note: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [fattureRes, statsRes] = await Promise.all([
        axios.get(`${API}/fatture-passive`, getAuthHeaders()),
        axios.get(`${API}/fatture-passive/stats`, getAuthHeaders())
      ]);
      setFatture(fattureRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento fatture");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotale = (imponibile, iva) => {
    const imp = parseFloat(imponibile) || 0;
    const ivaPercent = parseFloat(iva) || 22;
    return imp * (1 + ivaPercent / 100);
  };

  const handleImponibileChange = (value) => {
    const newTotale = calculateTotale(value, formData.iva);
    setFormData({ 
      ...formData, 
      imponibile: value,
      totale: newTotale.toFixed(2)
    });
  };

  const handleIvaChange = (value) => {
    const newTotale = calculateTotale(formData.imponibile, value);
    setFormData({ 
      ...formData, 
      iva: value,
      totale: newTotale.toFixed(2)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        imponibile: parseFloat(formData.imponibile),
        iva: parseFloat(formData.iva),
        totale: parseFloat(formData.totale)
      };

      if (editingFattura) {
        await axios.put(`${API}/fatture-passive/${editingFattura.id}`, data, getAuthHeaders());
        toast.success("Fattura aggiornata");
      } else {
        await axios.post(`${API}/fatture-passive`, data, getAuthHeaders());
        toast.success("Fattura creata");
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa fattura?")) return;
    try {
      await axios.delete(`${API}/fatture-passive/${id}`, getAuthHeaders());
      toast.success("Fattura eliminata");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleEdit = (fattura) => {
    setEditingFattura(fattura);
    setFormData({
      numero: fattura.numero,
      fornitore: fattura.fornitore,
      descrizione: fattura.descrizione,
      imponibile: fattura.imponibile.toString(),
      iva: fattura.iva.toString(),
      totale: fattura.totale.toString(),
      data_ricezione: fattura.data_ricezione,
      data_scadenza: fattura.data_scadenza,
      stato: fattura.stato,
      metodo_pagamento: fattura.metodo_pagamento || "",
      note: fattura.note || ""
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingFattura(null);
    setFormData({
      numero: "",
      fornitore: "",
      descrizione: "",
      imponibile: "",
      iva: "22",
      totale: "",
      data_ricezione: new Date().toISOString().split("T")[0],
      data_scadenza: "",
      stato: "ricevuta",
      metodo_pagamento: "",
      note: ""
    });
  };

  const filteredFatture = fatture.filter(f => {
    const matchesSearch = f.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         f.fornitore.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         f.descrizione.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStato = statoFilter === "all" || f.stato === statoFilter;
    return matchesSearch && matchesStato;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="fatture-passive-page">
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title">Fatture Passive</h1>
          <p className="page-subtitle">Gestione fatture ricevute da fornitori</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#FF3B30] hover:bg-[#FF2D20] text-white rounded-full px-6"
              data-testid="add-fattura-passiva-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Fattura
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-2xl border-0 shadow-2xl sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-[#1D1D1F]">
                {editingFattura ? "Modifica Fattura" : "Nuova Fattura Passiva"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Numero Fattura</Label>
                  <Input
                    value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    placeholder="FAT-FORN-001"
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="fattura-numero"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Fornitore</Label>
                  <Input
                    value={formData.fornitore}
                    onChange={(e) => setFormData({ ...formData, fornitore: e.target.value })}
                    placeholder="Nome fornitore"
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="fattura-fornitore"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Descrizione</Label>
                <Input
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                  placeholder="Descrizione acquisto/servizio"
                  className="h-11 rounded-xl border-[#D2D2D7]"
                  data-testid="fattura-descrizione"
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Imponibile (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.imponibile}
                    onChange={(e) => handleImponibileChange(e.target.value)}
                    placeholder="0,00"
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="fattura-imponibile"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">IVA (%)</Label>
                  <Select value={formData.iva} onValueChange={handleIvaChange}>
                    <SelectTrigger className="h-11 rounded-xl border-[#D2D2D7]" data-testid="fattura-iva">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="4">4%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="22">22%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Totale (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.totale}
                    onChange={(e) => setFormData({ ...formData, totale: e.target.value })}
                    placeholder="0,00"
                    className="h-11 rounded-xl border-[#D2D2D7] bg-[#F5F5F7]"
                    data-testid="fattura-totale"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Data Ricezione</Label>
                  <Input
                    type="date"
                    value={formData.data_ricezione}
                    onChange={(e) => setFormData({ ...formData, data_ricezione: e.target.value })}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="fattura-data-ricezione"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Data Scadenza</Label>
                  <Input
                    type="date"
                    value={formData.data_scadenza}
                    onChange={(e) => setFormData({ ...formData, data_scadenza: e.target.value })}
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="fattura-data-scadenza"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Stato</Label>
                  <Select value={formData.stato} onValueChange={(v) => setFormData({ ...formData, stato: v })}>
                    <SelectTrigger className="h-11 rounded-xl border-[#D2D2D7]" data-testid="fattura-stato">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                      {STATI_FATTURA.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Metodo Pagamento</Label>
                  <Input
                    value={formData.metodo_pagamento}
                    onChange={(e) => setFormData({ ...formData, metodo_pagamento: e.target.value })}
                    placeholder="Bonifico, Carta, etc."
                    className="h-11 rounded-xl border-[#D2D2D7]"
                    data-testid="fattura-metodo"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Note</Label>
                <Input
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Note aggiuntive..."
                  className="h-11 rounded-xl border-[#D2D2D7]"
                  data-testid="fattura-note"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1 h-11 rounded-full border-[#D2D2D7]"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-11 bg-[#FF3B30] hover:bg-[#FF2D20] text-white rounded-full"
                  data-testid="save-fattura-btn"
                >
                  {editingFattura ? "Aggiorna" : "Salva"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card className="stat-card card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#FF3B30]" />
              </div>
              <div>
                <p className="text-xs text-[#86868B] uppercase tracking-wider">Totale Fatture</p>
                <p className="text-xl font-semibold text-[#1D1D1F]">{stats?.totale_fatture || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FF9500]/10 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#FF9500]" />
              </div>
              <div>
                <p className="text-xs text-[#86868B] uppercase tracking-wider">Da Pagare</p>
                <p className="text-xl font-semibold text-[#FF9500]">{formatCurrency(stats?.da_pagare || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#34C759]/10 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[#34C759]" />
              </div>
              <div>
                <p className="text-xs text-[#86868B] uppercase tracking-wider">Pagate</p>
                <p className="text-xl font-semibold text-[#34C759]">{formatCurrency(stats?.totale_pagate || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card card-hover">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-[#FF3B30]" />
              </div>
              <div>
                <p className="text-xs text-[#86868B] uppercase tracking-wider">Scadute</p>
                <p className="text-xl font-semibold text-[#FF3B30]">{formatCurrency(stats?.totale_scadute || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868B]" />
              <Input
                placeholder="Cerca fatture..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-xl border-[#D2D2D7]"
                data-testid="search-fatture"
              />
            </div>
            <Select value={statoFilter} onValueChange={setStatoFilter}>
              <SelectTrigger className="w-40 h-11 rounded-xl border-[#D2D2D7]" data-testid="filter-stato">
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent className="bg-white rounded-xl border-0 shadow-lg">
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {STATI_FATTURA.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        <CardContent className="p-0">
          <Table data-testid="fatture-passive-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">N. Fattura</TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">Fornitore</TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">Data</TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">Scadenza</TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">Totale</TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">Stato</TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFatture.length > 0 ? (
                filteredFatture.map((fattura) => {
                  const stato = STATI_FATTURA.find(s => s.value === fattura.stato) || STATI_FATTURA[0];
                  return (
                    <TableRow key={fattura.id} className="hover:bg-[#F5F5F7]/30">
                      <TableCell className="px-6 py-4 font-medium text-[#1D1D1F]">{fattura.numero}</TableCell>
                      <TableCell className="px-6 py-4">
                        <p className="text-[#1D1D1F]">{fattura.fornitore}</p>
                        <p className="text-sm text-[#86868B] truncate max-w-[200px]">{fattura.descrizione}</p>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-[#86868B]">
                        {formatDate(fattura.data_ricezione)}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-[#86868B]">
                        {formatDate(fattura.data_scadenza)}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right font-semibold text-[#FF3B30]">
                        {formatCurrency(fattura.totale)}
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${stato.color}`}>
                          {stato.label}
                        </span>
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(fattura)}
                            className="h-9 w-9 rounded-lg hover:bg-[#007AFF]/10"
                            data-testid={`edit-fattura-${fattura.id}`}
                          >
                            <Pencil className="w-4 h-4 text-[#007AFF]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(fattura.id)}
                            className="h-9 w-9 rounded-lg hover:bg-[#FF3B30]/10"
                            data-testid={`delete-fattura-${fattura.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-[#FF3B30]" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-[#86868B]">
                    Nessuna fattura trovata
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default FatturePassive;
